import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  classifySignedBand,
  formatSignedValue,
  IMPACT_BAND_CUTOFFS,
  SignedBandPill,
  signedValueTextColorClass,
} from "./signedBand.js";

interface SignedImpactCellProps {
  /** Signed, volume-weighted delta from the scope-wide baseline */
  value: number | null;
  /** Suffix rendered after the number (e.g. `"pts"` for CX score,
   *  `"pp"` for win rate). Omitted = no suffix. */
  unit?: string;
  /** Decimal places on the signed number. Defaults to 2. */
  decimals?: number;
}

/**
 * Shared cell for every Topics-page metric expressed as a signed,
 * volume-weighted delta: a banded pill placing the topic on the
 * very-negative↔very-positive scale, alongside the signed number. Both
 * the band and the number key off the same `value`, so the pill always
 * agrees with the figure beside it. Renders `—` when the topic has no
 * data.
 */
export const SignedImpactCell: FC<SignedImpactCellProps> = ({
  value,
  unit,
  decimals,
}) => {
  if (value === null) {
    return <span className="text-sm text-gray-400">—</span>;
  }
  const band = classifySignedBand(value, IMPACT_BAND_CUTOFFS);
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <SignedBandPill band={band} />
      <span
        className={twMerge(
          "text-xs font-medium",
          signedValueTextColorClass(band),
        )}
      >
        {formatSignedValue(value, decimals)}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
};
